import { inject, injectable } from 'inversify';
import { IApiService, EventFilter, IEventService, EventOrderBy, OrderByMode } from '@/interfaces/Services';
import { auth } from '@/store/modules';
import { EventModel } from '@/models/event';
import { serializeMap } from '@/utils/transformers';
import DependencyIdentifiers from '@/ioc/DependencyIdentifiers';

export type PageEventResponse = {
  data: EventModel[],
  meta: { total: number; }
};

@injectable()
export default class EventService implements IEventService {
  private _api: IApiService;

  constructor(@inject(DependencyIdentifiers.ApiService) api: IApiService) {
    this._api = api;
  }

  async getMany(
    page: number = 1,
    size: number = 20,
    filter: EventFilter = {},
    orderBy?: Map<EventOrderBy, OrderByMode>,
  ): Promise<[EventModel[], number]> {
    let query = `/events?size=${size}&page=${page}`;
    query += this.getFilterQueryParams(filter);
    if (orderBy && orderBy.size > 0) {
      query += `&orderBy=${this.serializeOrderBy(orderBy)}`;
    }

    const res = await this._api.get<PageEventResponse>(query);
    return [res.data, res.meta.total];
  }

  async update(id: string, data: object): Promise<EventModel> {
    return await this._api
      .patch<{data: EventModel}>(`/events/${id}`, { data })
      .then(res => res.data);
  }

  private getFilterQueryParams(filter: EventFilter): string {
    let query = '';
    if (!!filter.patientId) {
      query += `&filter[patientId]=${filter.patientId}`;
    }
    if (!!filter.doctorId) {
      query += `&filter[doctorId]=${filter.doctorId}`;
    }

    if (!!filter.types) {
      query += `&filter[types]=${filter.types.join(',')}`;
    }

    if (!!filter.statuses) {
      query += `&filter[statuses]=${filter.statuses.join(',')}`;
    }

    if (!!filter.from) { query += `&filter[from]=${filter.from}`; }
    if (!!filter.until) { query += `&filter[until]=${filter.until}`; }

    query = `${query}` + `&filter[centerId]=${auth.currentCenterId}`;
    if (!!filter.searchQuery) {
      query += `&filter[searchQuery]=${filter.searchQuery}`;
    }

    return query;
  }

  private serializeOrderBy(orderBy: Map<EventOrderBy, OrderByMode>) {
    return serializeMap(orderBy);
  }
}
